import type React from "react"
import { useId } from "react"
import { classNames } from "../../../utils"
import { CenterAligned } from "../../CenterAligned"

// Regex for extracting last point coordinates from path data
const LAST_POINT_REGEX = /([0-9.-]+),([0-9.-]+)$/

type Props = {
  className?: string
  children?: React.ReactNode
}

// Helper function (optional, but keeps path data cleaner)
function createFillPathData(curvePathData: string, bottomY: number): string {
  const lastPointMatch = curvePathData.match(LAST_POINT_REGEX)
  if (!lastPointMatch) {
    return curvePathData
  }
  const lastX = lastPointMatch[1]
  // Simple bottom line: Go down from last point, left along bottom, close path
  const bottomLine = `L${lastX},${bottomY} L0,${bottomY} Z`
  return `${curvePathData} ${bottomLine}`
}

export function ChartSkeleton({ className, children }: Props) {
  const gradientBaseId = useId()
  const gradientId = `${gradientBaseId}-skeletonGradient`
  const curvePath =
    "M0,160.135C11.083,162.342,22.167,164.55,33.25,166.877C44.333,169.204,55.417,171.686,66.5,174.096C77.583,176.506,88.667,178.996,99.75,181.338C110.833,183.68,121.917,186.022,133,188.148C144.083,190.275,155.167,192.32,166.25,194.098C177.333,195.876,188.417,197.497,199.5,198.814C210.583,200.131,221.667,201.227,232.75,202C243.833,202.774,254.917,203.456,266,203.456C277.083,203.456,288.167,203.334,299.25,203.09C310.333,202.845,321.417,201.923,332.5,200.925C343.583,199.926,354.667,198.61,365.75,197.097C376.833,195.584,387.917,193.779,399,191.847C410.083,189.915,421.167,187.734,432.25,185.504C443.333,183.275,454.417,180.855,465.5,178.468C476.583,176.081,487.667,173.575,498.75,171.18C509.833,168.785,520.917,166.35,532,164.098C543.083,161.846,554.167,159.635,565.25,157.668C576.333,155.7,587.417,153.852,598.5,152.292C609.583,150.732,620.667,149.363,631.75,148.31C642.833,147.256,653.917,146.336,665,145.97C676.083,145.604,687.167,145.421,698.25,145.421C709.333,145.421,720.417,145.981,731.5,146.697C742.583,147.413,753.667,148.451,764.75,149.717C775.833,150.983,786.917,152.638,798,154.292"

  const viewBoxWidth = 798
  const viewBoxHeight = 350

  const fillPath = createFillPathData(curvePath, viewBoxHeight)

  return (
    <CenterAligned className={classNames("relative", className)}>
      <CenterAligned className={classNames("z-[1] size-full")}>
        {children}
      </CenterAligned>
      <svg
        className={classNames(
          "absolute inset-0 size-full",
          children && "opacity-60",
        )}
        preserveAspectRatio="none"
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" x2="0%" y1="0%" y2="100%">
            <stop
              offset="0%"
              stopColor="rgb(var(--color-bg-additional-2))"
              stopOpacity="0.8"
            />
            <stop
              offset="100%"
              stopColor="rgb(var(--color-bg-additional-2))"
              stopOpacity="0.2"
            />
          </linearGradient>
        </defs>

        {/* This rect isn't strictly needed with preserveAspectRatio="none" but is harmless */}
        {/* <rect fill="transparent" height="100%" width="100%" x="0" y="0" /> */}

        <g>
          {/* The curve line at the top */}
          <path
            className={classNames(
              "stroke-bg-additional-2",
              !children && "animate-pulse",
            )}
            d={curvePath}
            fill="transparent"
            strokeWidth="2"
            // IMPORTANT: Keep stroke width consistent when stretching/squashing
            vectorEffect="non-scaling-stroke"
          />

          {/* The gradient fill area */}
          <path
            className={classNames(!children && "animate-pulse")}
            d={fillPath}
            fill={`url(#${gradientId})`}
          />
        </g>
      </svg>
    </CenterAligned>
  )
}
